import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { Injectable } from '@angular/core';
import { PathData } from '../core/interfaces/eb-notification-wrapper';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  constructor() {}
}

export function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return hours + ':' + minutes + ' ' + ampm;
}

export function formatDate(date, showTime: boolean = false): any {
  let month = date.getMonth() + 1;
  let day = date.getDate();
  if (month < 10) {
    month = '0' + month;
  }
  if (day < 10) {
    day = '0' + day;
  }
  const time = showTime ? formatAMPM(date) : '';
  return day + '-' + month + '-' + date.getFullYear() + ' ' + time;
}

export function refreshForm(form: FormGroup) {
  if (form.invalid) {
    for (const contl in form.controls) {
      if (contl) {
        const s = form.get(contl);
        s.markAsDirty();
        s.markAsTouched();
        s.updateValueAndValidity();
      }
    }
  }
}

export function findInvalidControls(fg: FormGroup) {
  const invalid = [];
  const controls = fg.controls;
  for (const name in controls) {
    if (controls[name].invalid) {
      invalid.push({ name, err: controls[name].errors });
    }
  }
  console.log(invalid);
  return invalid;
}

export function trimStringValues(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach((key) => {
    const control: AbstractControl = formGroup.controls[key];
    if (typeof control.value === 'string') {
      const trimmedValue: string = control.value.trim();
      control.setValue(trimmedValue);
    }
  });
}

export function isKeycloakId(inputString) {
  const keycloakIdRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return keycloakIdRegex.test(inputString);
}

export function noWhitespaceValidator(control: FormControl) {
  const value = control.value;
  if (typeof value === 'string' && value.trim().length === 0) {
    return { whitespace: true };
  }
  return null;
}

export function getAddedUserIds(updatedData, original) {
  if (
    !updatedData ||
    !original ||
    !updatedData.workFlowActivity ||
    !original.workFlowActivity
  ) {
    return [];
  }
  const addedUserIds = [];
  for (const wf of updatedData?.workFlowActivity) {
    const owf = original.workFlowActivity.find((o) => o.groupId === wf.groupId);
    if (!owf) {
      addedUserIds.push(...wf.approvers.map((wa) => wa.userId));
      continue;
    }

    for (const wa of wf.approvers) {
      const owfApprover = owf.approvers.find((o) => o.userId === wa.userId);
      if (!owfApprover) {
        addedUserIds.push(wa.userId);
      }
    }
  }
  return addedUserIds;
}

export function makeValuesNullExceptId(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  for (const prop in obj) {
    if (prop !== 'id') {
      obj[prop] = null;
    }
  }
  return obj;
}

export function filterApproversByField(wfa, groupedUsers) {
  if (groupedUsers) {
    return wfa.approvers
      .filter((approver) =>
        groupedUsers.some((user) => user.id === approver.userId)
      )
      .map((approver) => ({ id: approver.userId }));
  } else {
    return [];
  }
}

export const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['clean'],
    ['link'],
  ],
};

export function prepareMir3Data(data) {
  return {
    incidentId: data?.id,
    notificationTitle: data?.templateTitle || '',
    notificationDescription: data?.description || '',
    createdOn: new Date(),
    updatedBy: new Date(),

    individualRecipientIdsList: getUserIds(data),
    groupIdsList: getGroupIds(data),
    devicePriority: [
      ...data?.devices?.map((s) => ({
        deviceType: s.deviceName,
        priority: s.devicePriority,
      })),
    ],

    validateRecipient: data?.validationRecipient?.toString() || 'false',
    replayMessages: data?.replayMessages?.toString() || 'false',
    textContactOnce: data?.textContactOnce?.toString() || 'false',
    confirmResponse: data?.confirmResponse?.toString() || 'false',
    playGreeting: data?.playGreeting?.toString() || 'false',
    expeditedDelivery: data?.expeditedDelivery?.toString() || 'false',
    recipientListenedToEntireMessage:
      data?.listenEntireMessage?.toString() || 'false',
    recipientListenedToPartialMessage:
      data?.listenPartialMessage?.toString() || 'false',
    entireMessageLeftOnVoiceMail:
      data?.voiceMailEntireMessage?.toString() || 'false',
    partialMessageLeftOnVoiceMail:
      data?.voiceMailPartialMessage?.toString() || 'false',
    initiatorAlias: data.alias ? data.alias : 'AutoBCM',
    broadcastDuration: data?.broadcastDuration?.toString() || '0',
    contactCycleDelay:
      data?.contactCycleDelay?.getHours() +
        ':' +
        data?.contactCycleDelay?.getMinutes() || '00:00',
    deviceDelay:
      data?.deviceDelay?.getHours() + ':' + data?.deviceDelay?.getMinutes() ||
      '00:00',
    contactAttemptCycles: data?.contactAttemptCycles?.toString() || '0',
    leaveMessage: data?.leaveMessage?.label || 'OFF',
    messages: getMessage(data),
    responses: getResponses(data),
  };
}

function getMessage(data) {
  return [
    {
      msgtype: 'Default',
      locale: 'EN_US',
      value: data.messages.find((s) => s.language === 'MG_EN').text,
    },
  ];
}

function getResponses(data) {
  if (data.messages.length) {
    return data.messages
      ?.find((s) => s.language === 'MG_EN')
      .options?.map((ad) =>
        [ad].map((s) => ({ locale: 'EN_US', value: s.respOption }))
      );
  } else {
    return [];
  }
}

function getGroupIds(templateData: any) {
  return templateData.groups
    ? templateData.groups.includes(',')
      ? templateData.groups.split(',').filter(Boolean)
      : [templateData.groups]
    : [];
}

export function getUserIds(templateData) {
  let userIds: string[] = [];

  // users
  if (templateData.users) {
    const usersToAdd = Array.isArray(templateData.users)
      ? templateData.users
      : templateData.users.split(',');
    userIds = [...userIds, ...usersToAdd];
  }

  // for saCmImTeams
  if (templateData.saCmImTeams && templateData.saCmImTeams.length > 0) {
    templateData.saCmImTeams.map((saCmImTeam) => {
      if (
        saCmImTeam.saCmImTeamMembers &&
        saCmImTeam.saCmImTeamMembers.length > 0
      ) {
        saCmImTeam.saCmImTeamMembers.map((saCmImTeamMember) => {
          userIds = [...userIds, saCmImTeamMember.staffId];
        });
      }
    });
  }

  // for saCmCmTeams
  if (templateData.saCmCmTeams && templateData.saCmCmTeams.length > 0) {
    templateData.saCmCmTeams.map((saCmCmTeam) => {
      if (
        saCmCmTeam.saCmCmTeamMembers &&
        saCmCmTeam.saCmCmTeamMembers.length > 0
      ) {
        saCmCmTeam.saCmCmTeamMembers.map((saCmCmTeamMember) => {
          userIds = [...userIds, saCmCmTeamMember.staffId];
        });
      }
    });
  }

  // for saCmIrTeams
  if (templateData.saCmIrTeams && templateData.saCmIrTeams.length > 0) {
    templateData.saCmIrTeams.map((saCmIrTeam) => {
      if (
        saCmIrTeam.saCmIrTeamMembers &&
        saCmIrTeam.saCmIrTeamMembers.length > 0
      ) {
        saCmIrTeam.saCmIrTeamMembers.map((saCmIrTeamMember) => {
          userIds = [...userIds, saCmIrTeamMember.staffId];
        });
      }
    });
  }

  // for saCmCrTeams
  if (templateData.saCmCrTeams && templateData.saCmCrTeams.length > 0) {
    templateData.saCmCrTeams.map((saCmCrTeam) => {
      if (
        saCmCrTeam.saCmCrTeamMembers &&
        saCmCrTeam.saCmCrTeamMembers.length > 0
      ) {
        saCmCrTeam.saCmCrTeamMembers.map((saCmCrTeamMember) => {
          userIds = [...userIds, saCmCrTeamMember.staffId];
        });
      }
    });
  }
  return [...new Set(userIds)];
}

export function objectWithKeysAndValuesValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;

    if (
      value === null ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      Object.keys(value).length === 0
    ) {
      return { invalidObject: true };
    }

    return null; // Validation passed
  };
}

export function prepareEbData(data) {
  const responses = getEbResponses(data);
  return {
    wrapper: {
      type: responses.length ? 'Polling' : 'Standard',
      message: getEbMessage(data, responses),
      broadcastContacts: {
        externalIds: [],
      },
      broadcastSettings: {
        senderEmail: data?.alias
          ? data?.alias?.name
          : data?.initiatorAlias?.name || 'AutoBCM',
        duration: +data?.broadcastDuration || 0,
        contactCycles: +data?.contactAttemptCycles || 0,
        cycleInterval: data?.contactCycleDelay?.label.toString(),
        deliveryMethodInterval: data?.deviceDelay?.label.toString(),
        language: 'en_US',
        voiceMailOption: data?.leaveMessage.code,
        deliverPaths: getEbDevices(data),
      },
    },
    userIds: convertString(data?.users),
    groupIds: convertString(data?.groups),
    locations: data.locations,
    countries: data.countries,
    states: data.states,
    cities: data.cities,
    zipCodes: data.zipCodes,
  };
}

function getEbMessage(data, responses) {
  const useCustomEmail: boolean = data.messages.some(
    (s) => s.language === 'MG_EN'
  );
  const textMessage = data.messages.some((s) => s.language === 'MG_EN_SMS')
    ? data.messages.find((s) => s.language === 'MG_EN_SMS').text
    : 'BMC Alerts';
  const emailContent = data.messages.some((s) => s.language === 'MG_EN')
    ? data.messages.find((s) => s.language === 'MG_EN').text
    : 'BMC Alerts';

  return {
    title: data.templateTitle,
    textMessage,
    useCustomEmail,
    rtfContent: useCustomEmail ? getEmailText(emailContent) : null,
    questionaire: {
      answers: responses.map((s) => ({ name: s.value })),
    },
  };
}

function getEmailText(message) {
  return `<style>body{font-family:Arial,Helvetica,sans-serif,serif,EmojiFont;font-size:10.5pt;}</style><table border=0 cellpadding=0 cellspacing=0 width=100%><tr><td align=center><table border=0 cellpadding=0 cellspacing=0 width=600 style="margin:0 auto"><tr><td valign=top>${message}`;
}

function getEbDevices(data) {
  return (data?.devices || [])
    .sort((a, b) => a.devicePriority - b.devicePriority)
    .map(({ deviceCode }) => ({
      pathId: deviceCode,
      pathType: getPathTypeByPathId(deviceCode),
    }));
}

function getPathTypeByPathId(pathId) {
  for (const item of PathData) {
    if (item.pathId === pathId) {
      return item.pathType;
    }
  }
  return null;
}

function getEbResponses(data) {
  if (data.messages.length) {
    const lang = data.messages.some((s) => s.language === 'MG_EN')
      ? 'MG_EN'
      : 'MG_EN_SMS';
    return data.messages
      ?.find((s) => s.language === lang)
      .options?.map((ad) => ({ value: ad.respOption }));
  } else {
    return [];
  }
}

function convertString(data: any) {
  let usersArray = [];
  if (typeof data === 'string') {
    usersArray = data.split(',');
  } else if (Array.isArray(data)) {
    usersArray = data;
  }
  return usersArray;
}

export const SECURE_RANDOM_NUMBER = {
  get value(): number {
    return crypto.getRandomValues(new Uint32Array(1))[0] / (0xffffffff + 1);
  },
};
